import {
  CAlert,
  CButton,
  CCol,
  CForm,
  CFormGroup,
  CInput,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CSelect,
} from "@coreui/react";
import React, { useState, useEffect } from "react";
import {
  useAutomationConfigTemplate,
  useImportAutomationConfig,
  useNewAutomationConfig,
} from "src/views/products/cap-ai/cap-automation/services/AutomationListServices";
import { extractErrorDetail } from "src/views/products/parts-ai/utils/errorDetail";

import CIcon from "@coreui/icons-react";
import { Loading } from "src/reusable/LoadingSpinner";
import LoadingOverlay from "react-loading-overlay-ts";
import PropTypes from "prop-types";
import { useGetModel } from "../models/ModelService";
import { Alert } from "@mui/material";
import {
  isLlmModelVersion,
  isLlmTemplate,
} from "src/views/products/cap-ai/cap-automation/llm/llmUtils";
import { useLlmScreeningEnabled } from "src/helpers/licensedRolloutGates";

const ModelVersionSelect = (props) => {
  const [selectedModelVersion, setSelectedModelVersion] = useState("");
  const [aVersionIsNotDeployed, setAVersionIsNotDeployed] = useState(false);

  useEffect(() => {
    props.handleSelectedModelVersion(selectedModelVersion);
  }, [selectedModelVersion]);

  const { data: model } = useGetModel(props.modelId);
  let modelVersions = [];
  if (model?.versions) {
    // Only a classifier template's config picks a version here, and the
    // backend 422s a classifier config on a chat LLM (text-generative)
    // version, so a mixed-lineage model offers its classifier versions only.
    modelVersions = model.versions.filter(
      (modelVersion) => !isLlmModelVersion(modelVersion),
    );
    modelVersions.sort((a, b) => (a.name > b.name ? 1 : -1)); //sort by name
  }
  useEffect(() => {
    // check all model versions to see if the model.status_fk is not deployed
    if (modelVersions) {
      const undeployedModelVersion = modelVersions.some(
        (modelVersion) => modelVersion.status_fk !== "deployed",
      );
      if (undeployedModelVersion) {
        setAVersionIsNotDeployed(true);
      }
    }
  }, [model]);
  return (
    <>
      {modelVersions && (
        <>
          {aVersionIsNotDeployed && (
            <Alert severity="warning" style={{ marginBottom: "10px" }}>
              Some Model Versions Might Not Be Deployed
            </Alert>
          )}
          <CSelect
            id="model-version-select"
            data-testid="cap-flow-modal-config-select-version"
            name="model-version-select"
            value={selectedModelVersion ? selectedModelVersion : ""}
            onChange={(e) => {
              setSelectedModelVersion(
                e.target.value === "" ? null : e.target.value,
              );
            }}
          >
            <option value="">Select a model version</option>
            {modelVersions.map((modelVersion) => {
              // if we have a model version that is in the undeployed state, we need to warn the user
              if (modelVersion.status_fk !== "deployed") {
                // this case we want to set the option color to light red
                return (
                  <option
                    style={{ backgroundColor: "#FFCCCB" }}
                    key={modelVersion.id}
                    value={modelVersion.id}
                  >
                    {`${model.name} version ${modelVersion.version_number} - (Undeployed)`}
                  </option>
                );
              } else {
                return (
                  <option key={modelVersion.id} value={modelVersion.id}>
                    {`${model.name} version ${modelVersion.version_number}`}
                  </option>
                );
              }
            })}
          </CSelect>
        </>
      )}
    </>
  );
};

ModelVersionSelect.propTypes = {
  modelId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  handleSelectedModelVersion: PropTypes.func.isRequired,
};

/**
 * The "Model Version" step shared by the create and import modals. A
 * classifier template's config picks one of the template model's versions;
 * an llm template's config takes none (the screening persona linked on the
 * config owns the model), so the step is skipped and, with the LLM screening
 * gate open, replaced by a pointer to where the persona is set.
 */
const ConfigModelVersionField = ({
  automationConfigTemplate,
  isTemplateLoading,
  isTemplateLoaded,
  onSelectModelVersion,
}) => {
  const llmScreeningEnabled = useLlmScreeningEnabled();
  if (isLlmTemplate(automationConfigTemplate)) {
    return llmScreeningEnabled ? (
      <Alert
        severity="info"
        style={{ marginBottom: "1rem" }}
        data-testid="cap-flow-modal-config-llm-hint"
      >
        This template screens with an LLM, so the config takes no model version.
        After creating it, link a screening persona (which supplies the model
        and instructions) via the &quot;Configure LLM Screening&quot; node in
        the AutoFlow.
      </Alert>
    ) : null;
  }
  const model =
    automationConfigTemplate?.automation_config_template_models?.[0];
  return (
    <CFormGroup row>
      <CLabel className="col-sm-2 col-form-label">Model Version</CLabel>
      <CCol sm={10}>
        {model && (
          <ModelVersionSelect
            modelId={model.model_fk}
            handleSelectedModelVersion={onSelectModelVersion}
          />
        )}
        {isTemplateLoading && "Loading model details..."}

        {!model && isTemplateLoaded && (
          <Alert severity="warning">
            Please associate a model with the &quot;
            <strong>{automationConfigTemplate.name}</strong>&quot; automation
            template to select a model version.
          </Alert>
        )}
      </CCol>
    </CFormGroup>
  );
};

ConfigModelVersionField.propTypes = {
  automationConfigTemplate: PropTypes.object,
  isTemplateLoading: PropTypes.bool,
  isTemplateLoaded: PropTypes.bool,
  onSelectModelVersion: PropTypes.func.isRequired,
};

export const CreateAutomationConfigModal = (props) => {
  const [acName, setAcName] = useState("");
  const [acModelVersionId, setAcModelVersionId] = useState(null);
  const [errorMessage, setErrorMessage] = React.useState(null);

  const {
    mutate: createAutomationConfig,
    isLoading: isCreateAutomationConfigLoading,
    status: createAutomationConfigStatus,
    isError: isCreateAutomationConfigError,
    error: createAutomationConfigError,
    data: newAutomationConfig,
    reset: resetCreateAutomationConfig,
  } = useNewAutomationConfig();

  const {
    data: automationConfigTemplate,
    isSuccess: autoConfigTemplateSuccess,
    isLoading: autoConfigTemplateLoading,
  } = useAutomationConfigTemplate(props.automationConfigTemplateId);
  const isLlm = isLlmTemplate(automationConfigTemplate);

  const resetAfterClose = () => {
    setAcName("");
    setAcModelVersionId(null);
    setErrorMessage(null);
    resetCreateAutomationConfig();
  };
  // If there was a error creating the dataset, get rid of it!
  if (isCreateAutomationConfigError) {
    let newErrorMessage = String(createAutomationConfigError);
    if (createAutomationConfigError?.response?.data?.detail) {
      newErrorMessage =
        "Server Error " +
        createAutomationConfigError.response.status +
        ": " +
        JSON.stringify(createAutomationConfigError.response.data.detail);
    }
    if (newErrorMessage !== errorMessage) {
      setErrorMessage(newErrorMessage);
    }
  }

  const validationErrors = () => {
    let errors = [];
    // an llm template's config has no model version to pick
    if (!isLlm && !acModelVersionId) {
      errors.push("Must select a model version.");
    }
    return errors;
  };

  const onSubmit = () => {
    let errors = validationErrors();
    if (errors.length === 0) {
      setErrorMessage(null);
      createAutomationConfig({
        automationConfigTemplateId: props.automationConfigTemplateId,
        // the service drops it for an llm template (the backend 422s on one)
        modelVersionId: acModelVersionId,
        screeningKind: automationConfigTemplate?.screening_kind,
        name: !acName || acName == "" ? null : acName,
      });
    } else {
      setErrorMessage(errors.map((error, index) => <p key={index}>{error}</p>));
    }
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      centered
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h4>Create a new automation config</h4>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="create-automation-close-button"
            data-testid="cap-flow-modal-config-btn-close"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <LoadingOverlay
        active={isCreateAutomationConfigLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Creating automation config..."
      >
        {createAutomationConfigStatus === "success" ? (
          <CModalBody style={{ textAlign: "center", padding: "1.5rem" }}>
            <span id="create-automation-success-message">
              {newAutomationConfig?.data?.name} has been created!
            </span>
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
              <CForm>
                <CFormGroup row>
                  <CLabel className="col-sm-2 col-form-label">Name</CLabel>
                  <CCol sm={10}>
                    <CInput
                      id="create-new-automation-name-input"
                      data-testid="cap-flow-modal-config-input-name"
                      type="text"
                      name="name"
                      placeholder="Name"
                      value={acName}
                      onChange={(e) => setAcName(e.target.value)}
                    />
                  </CCol>
                </CFormGroup>
                <ConfigModelVersionField
                  automationConfigTemplate={automationConfigTemplate}
                  isTemplateLoading={autoConfigTemplateLoading}
                  isTemplateLoaded={autoConfigTemplateSuccess}
                  onSelectModelVersion={setAcModelVersionId}
                />
              </CForm>
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" style={{ textAlign: "right" }}>
                <CButton
                  id="submit-create-new-automation-button"
                  data-testid="cap-flow-modal-config-btn-submit"
                  color="success"
                  onClick={() => onSubmit()}
                >
                  Create New Automation Configuration
                </CButton>
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

CreateAutomationConfigModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  automationConfigTemplateId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
};

export const ImportAutomationConfigModal = (props) => {
  /**
   * Prompt for the name and model version of a config imported from a JSON
   * file (mirrors CreateAutomationConfigModal). The rule values themselves
   * come from the uploaded file (props.configJson).
   */
  const [acName, setAcName] = useState(props.defaultName ?? "");
  const [acModelVersionId, setAcModelVersionId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const {
    mutate: importConfig,
    isLoading: isImportLoading,
    status: importStatus,
    data: importResult,
    reset: resetImport,
  } = useImportAutomationConfig();

  const {
    data: automationConfigTemplate,
    isSuccess: autoConfigTemplateSuccess,
    isLoading: autoConfigTemplateLoading,
  } = useAutomationConfigTemplate(props.automationConfigTemplateId);
  const isLlm = isLlmTemplate(automationConfigTemplate);

  useEffect(() => {
    // Each newly picked file suggests its own name
    setAcName(props.defaultName ?? "");
  }, [props.defaultName]);

  const resetAfterClose = () => {
    setAcName("");
    setAcModelVersionId(null);
    setErrorMessage(null);
    resetImport();
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  const onSubmit = () => {
    if (!isLlm && !acModelVersionId) {
      setErrorMessage("Must select a model version.");
      return;
    }
    setErrorMessage(null);
    importConfig(
      {
        automationConfigTemplateId: props.automationConfigTemplateId,
        // the service drops it for an llm template (the backend 422s on one)
        modelVersionId: acModelVersionId,
        screeningKind: automationConfigTemplate?.screening_kind,
        name: !acName || acName === "" ? null : acName,
        configJson: props.configJson,
      },
      {
        onError: (error) => {
          setErrorMessage(`Config import failed: ${extractErrorDetail(error)}`);
        },
      },
    );
  };

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h4>Import an automation config</h4>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            data-testid="cap-flow-modal-import-btn-close"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <LoadingOverlay
        active={isImportLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Importing automation config..."
      >
        {importStatus === "success" ? (
          <CModalBody>
            <span data-testid="cap-flow-modal-import-success-message">
              {importResult?.automation_config?.name} has been imported as a new
              draft config!
            </span>
            {importResult?.warnings?.length > 0 && (
              <CAlert color="warning" style={{ marginTop: "1em" }}>
                <strong>Warnings:</strong>
                <ul style={{ marginBottom: 0 }}>
                  {importResult.warnings.map((warning, index) => (
                    <li key={`${index}-${warning}`}>{warning}</li>
                  ))}
                </ul>
              </CAlert>
            )}
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
              <CForm>
                <CFormGroup row>
                  <CLabel className="col-sm-2 col-form-label">Name</CLabel>
                  <CCol sm={10}>
                    <CInput
                      data-testid="cap-flow-modal-import-input-name"
                      type="text"
                      name="name"
                      placeholder="Name"
                      value={acName}
                      onChange={(e) => setAcName(e.target.value)}
                    />
                  </CCol>
                </CFormGroup>
                <ConfigModelVersionField
                  automationConfigTemplate={automationConfigTemplate}
                  isTemplateLoading={autoConfigTemplateLoading}
                  isTemplateLoaded={autoConfigTemplateSuccess}
                  onSelectModelVersion={setAcModelVersionId}
                />
              </CForm>
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" style={{ textAlign: "right" }}>
                <CButton
                  data-testid="cap-flow-modal-import-btn-submit"
                  color="success"
                  onClick={() => onSubmit()}
                >
                  Import Automation Configuration
                </CButton>
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

ImportAutomationConfigModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  automationConfigTemplateId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  configJson: PropTypes.object,
  defaultName: PropTypes.string,
};
